import { useEffect, useState, type MouseEvent, type ReactNode } from "react"
import {
  alpha,
  Badge,
  ClickAwayListener,
  Drawer,
  Paper,
  Popper,
  Stack,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"

import Chip from "./Chip"

const MIN_WIDTH = 200
const MOBILE_HIT_TARGET = 44

interface OverflowChipProps {
  /** How many items the chip hides. */
  count: number
  /** Names the chip and its list for a screen reader, such as "5 more power lines". */
  ariaLabel: string
  /** Marks the chip when something it hides needs a look. */
  dotColor?: string
  /** Only the hidden items, each in an `OverflowItem`, drawn the way they are next to the chip. */
  children: ReactNode
  "data-testid"?: string
}

/** One row of the list, with room to hit it. */
export const OverflowItem = ({ children }: { children: ReactNode }) => (
  <Stack
    role="listitem"
    direction="row"
    alignItems="center"
    gap={0.5}
    minHeight={{ xs: MOBILE_HIT_TARGET, md: 32 }}
    paddingX={1.25}
    paddingY={0.5}
  >
    {children}
  </Stack>
)

/**
 * The +N after the items a cell has room for. It opens a list of only the hidden
 * ones under the chip, or a bottom sheet on mobile.
 */
const OverflowChip = ({
  count,
  ariaLabel,
  dotColor,
  children,
  "data-testid": testId,
}: OverflowChipProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const open = Boolean(anchorEl)
  const handleClose = () => setAnchorEl(null)

  // The drawer closes on Esc by itself, the popper does not.
  useEffect(() => {
    if (!open || isMobile) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAnchorEl(null)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open, isMobile])

  // Following a link leaves the page behind, so the list goes with it.
  const handleListClick = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest("a")) handleClose()
  }

  const list = (
    <Stack role="list" aria-label={ariaLabel} padding={1} onClick={handleListClick}>
      {children}
    </Stack>
  )

  return (
    <>
      <Badge
        variant="dot"
        invisible={!dotColor}
        sx={{ "& .MuiBadge-badge": { backgroundColor: dotColor } }}
        data-testid="overflow-chip-dot"
      >
        <Chip
          label={`+${count}`}
          onClick={(event) => setAnchorEl(open ? null : event.currentTarget)}
          aria-label={ariaLabel}
          aria-expanded={open}
          data-testid={testId}
          sx={{
            borderColor: open ? theme.palette.primary.main : theme.palette.grey[400],
            color: theme.palette.text.primary,
            backgroundColor: alpha(theme.palette.text.primary, 0.1),
            // Draws the same, but a finger gets a bigger target.
            [theme.breakpoints.down("md")]: {
              position: "relative",
              overflow: "visible",
              "&::after": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                width: `max(100%, ${MOBILE_HIT_TARGET}px)`,
                height: MOBILE_HIT_TARGET,
                transform: "translate(-50%, -50%)",
              },
            },
          }}
        />
      </Badge>
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              backgroundColor: theme.mixins.floatingSurface.backgroundColor,
              borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
            },
          }}
        >
          {list}
        </Drawer>
      ) : (
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[
            { name: "offset", options: { offset: [0, 6] } },
            // Right-aligned with the chip when it would run off the right, above it when there is no room below.
            {
              name: "flip",
              options: { fallbackPlacements: ["bottom-end", "top-start", "top-end"] },
            },
          ]}
          sx={{ zIndex: theme.zIndex.modal }}
        >
          <ClickAwayListener
            onClickAway={(event) => {
              // The chip toggles it on its own.
              if (anchorEl?.contains(event.target as Node)) return
              handleClose()
            }}
          >
            <Paper
              elevation={3}
              sx={{
                ...theme.mixins.floatingSurface,
                minWidth: MIN_WIDTH,
                maxWidth: `calc(100vw - ${theme.spacing(4)})`,
              }}
            >
              {list}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  )
}

export default OverflowChip
